import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAuth } from '../../contexts/AuthContext';

const FormContainer = styled.div`
  background: white;
  padding: 2.5rem;
  border-radius: 20px;
  border: 2px solid #000;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  max-width: 400px;
  margin: 2rem auto;
  width: 100%;
  box-sizing: border-box;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 25px;
  font-size: 1rem;
  width: 100%;
  box-sizing: border-box;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
`;

const Button = styled.button`
  background: #000;
  color: white;
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 25px;
  font-size: 1rem;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #333;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  color: #000;
  font-weight: 600;
  font-size: 1.5rem;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  text-align: center;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 15px;
  font-size: 0.9rem;
`;

const LinkText = styled.p`
  text-align: center;
  margin-top: 1.5rem;
  color: #666;
  font-size: 0.9rem;
  
  a {
    color: #000;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const RecaptchaContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 1rem 0;
`;

const ErrorText = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  color: #000;
  font-weight: 500;
  display: block;
`;

const Register: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState('');
  
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  const validatePhone = (phoneNumber: string): boolean => {
    if (!phoneNumber.trim()) return true; // Optional field, empty is valid
    
    // Remove all non-digit characters for validation
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    
    // Phone number should be between 7 and 15 digits (international standard)
    if (digitsOnly.length < 7 || digitsOnly.length > 15) {
      return false;
    }
    
    // Check for valid phone number patterns
    // Must start with + or be all digits, and contain only valid phone characters
    const phoneRegex = /^[\+]?[\d\s\-\(\)]{7,20}$/;
    return phoneRegex.test(phoneNumber);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhone(value);
    
    if (value.trim() && !validatePhone(value)) {
      setPhoneError('Please enter a valid phone number (7-15 digits)');
    } else {
      setPhoneError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setPhoneError('');

    // Validate phone number if provided
    if (phone.trim() && !validatePhone(phone)) {
      setPhoneError('Please enter a valid phone number (7-15 digits)');
      setIsLoading(false);
      return;
    }

    // Check if reCAPTCHA is completed
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA verification');
      setIsLoading(false);
      return;
    }

    try {
      await register(email, password, phone || undefined, recaptchaToken);
      navigate('/');
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Registration failed');
      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
    if (error && error.includes('reCAPTCHA')) {
      setError('');
    }
  };

  return (
    <FormContainer>
      <Title>Create Account</Title>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <Form onSubmit={handleSubmit}>
        <InputGroup>
          <Label htmlFor="email">Email Address</Label>
          <Input
            id="email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </InputGroup>
        <InputGroup>
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </InputGroup>
        <InputGroup>
          <Label htmlFor="phone">Phone Number (Optional)</Label>
          <Input
            id="phone"
            type="tel"
            placeholder="Enter your phone number (e.g., +1 234 567 8900)"
            value={phone}
            onChange={handlePhoneChange}
          />
          {phoneError && <ErrorText>{phoneError}</ErrorText>}
        </InputGroup>
        <RecaptchaContainer>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
            onChange={handleRecaptchaChange}
            theme="light"
          />
        </RecaptchaContainer>
        <Button type="submit" disabled={isLoading || !recaptchaToken}>
          {isLoading ? 'Creating Account...' : 'Create Account'}
        </Button>
      </Form>
      <LinkText>
        Already have an account? <Link to="/login">Sign in here</Link>
      </LinkText>
    </FormContainer>
  );
};

export default Register;